import { Button, Divider, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import isEqual from "lodash/isEqual";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { Container, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useEventSeriesForCurrentBusiness } from "../hooks";
import styles from "../styles/settingsGeneralDetails.module.css";
import { BusinessInstance } from "../utils/businessClass";
import { UsStateInstance } from "../utils/usStateClass";
import { LoadingSpinner } from "./loadingSpinner";
import { SettingsImageField } from "./settingsImageFields";

interface BusinessExperienceFormProps {
  experience: any;
  isNew: boolean;
}

/**
 *
 * @param timeString Converts a time string to a DateTime object
 * Example input: "12:00 PM"
 */
const convertTimeStringToObject = (timeString: string) => {
  if (!timeString) {
    return { hour: 0, minute: 0, second: 0, millisecond: 0 };
  }

  const time = timeString.slice(0, timeString.length - 3);
  const amPm = timeString.slice(-2);
  const [hours, minutes] = time.split(":").map(Number);

  const getHours = (hours: number, amPm: string) => {
    switch (hours) {
      case 12:
        return amPm === "AM" ? 0 : 12;
      default:
        return hours + (amPm === "PM" ? 12 : 0);
    }
  };

  return {
    hour: getHours(hours, amPm),
    minute: minutes,
    second: 0,
    millisecond: 0,
  };
};

export const BusinessExperienceForm: React.FC<BusinessExperienceFormProps> = ({ experience, isNew }) => {
  const { activeBusiness, businessType, isLoading } = useSelector((state: any) => state.business);
  const { states, isLoading: statesLoading } = useSelector((state: any) => state.state);
  const { isLoading: experienceLoading } = useSelector((state: any) => state.experience);
  const { eventSeries } = useEventSeriesForCurrentBusiness();

  const initialFormState = {
    ...experience,
  };
  const [formData, setFormData] = useState(initialFormState);
  const [initialFormData, setInitialFormData] = useState({});
  const [showModal, setCanShowModal] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [updates, setUpdates] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (isNew) {
      setFormData(initialFormState);
    }
  }, [isNew]);

  useEffect(() => {
    if (!isNew) {
      setInitialFormData({
        ...initialFormState,
        ...updates,
      });
      setFormData({
        ...initialFormState,
        ...updates,
      });
    }
  }, [experience]);

  useEffect(() => {
    if (states.length === 0) {
      (async () => {
        await UsStateInstance.getUsStates();
      })();
    }
  }, [states.length]);

  useEffect(() => {
    setIsModified(!isEqual(formData, initialFormData));
  }, [formData, initialFormState]);

  // Autofill state if stateId is present
  useEffect(() => {
    if (!states.length) return;

    if (formData.stateId && !formData.state) {
      const state = states.find((state: any) => state.id === formData.stateId);
      setFormData((prevFormData: any) => ({
        ...prevFormData,
        state,
      }));
    }
  }, [formData, states]);

  useEffect(() => {
    if (isNew) {
      setCanSubmit(
        formData.hasOwnProperty("city") &&
          formData.hasOwnProperty("description") &&
          formData.hasOwnProperty("disclaimer") &&
          formData.hasOwnProperty("eventBeginDate") &&
          formData.hasOwnProperty("eventEndDate") &&
          formData.hasOwnProperty("startTime") &&
          formData.hasOwnProperty("endTime") &&
          formData.hasOwnProperty("headline") &&
          formData.hasOwnProperty("imageUrl") &&
          formData.hasOwnProperty("locationName") &&
          formData.hasOwnProperty("personRedeemLimit") &&
          formData.hasOwnProperty("redeemBeginDate") &&
          formData.hasOwnProperty("redeemEndDate") &&
          formData.hasOwnProperty("seedCost") &&
          formData.hasOwnProperty("state") &&
          formData.hasOwnProperty("street1") &&
          formData.hasOwnProperty("zip"),
      );
    }
  }, [formData, statesLoading]);

  const handleCloseModal = () => {
    setCanShowModal(false);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const submissionData = {
      ...formData,
      eventSeriesId: formData.eventSeriesId || null,
    };
    const flyerImg = formData.imageUrl;

    delete submissionData["imageUrl"];
    submissionData["experienceType"] = "redeem";

    const details = {
      businessId: activeBusiness.id,
      businessType: businessType,
      experience: submissionData,
      imageUrl: flyerImg,
    };

    const res = await BusinessInstance.addExperience(details);

    if (res) {
      setInitialFormData(res);
      setCanShowModal(true);
    } else {
      console.error("Something went wrong creating the experience");
    }
    return;
  };

  const handleUpdate = async (e: any) => {
    e.preventDefault();
    const getChangedFields = (initial: any, current: any) => {
      const changes: Record<string, any> = {};
      Object.keys(current).forEach((key) => {
        if (!isEqual(initial[key], current[key])) {
          changes[key] = current[key];
        }
      });
      return changes;
    };
    const updatedFields = getChangedFields(initialFormData, {
      ...formData,
      eventSeriesId: formData.eventSeriesId || null,
    });

    updatedFields["experienceType"] = "redeem";
    updatedFields["headline"] = formData.headline;

    const data = {
      data: updatedFields,
      id: formData.id,
      imageUrl: formData.imageUrl instanceof File ? formData.imageUrl : null,
    };

    try {
      const res = await BusinessInstance.updateExperience(data);
      if (res) {
        setFormData(res);

        setCanShowModal(true);
      } else {
      }
    } catch (error) {
      console.error("Error updating experience:", error);
    }
  };

  const handleChange = (e: any) => {
    const { name, value, type } = e.target;
    const isNumberInput = name.includes("Fee") || type === "number";
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "radio" ? value === "true" : isNumberInput ? parseFloat(value) || 0 : value,
    }));
  };

  const handleDateChange = (field: string) => (date: DateTime | null) => {
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      [field]: date?.setZone("utc")?.toISODate() ?? null,
    }));
  };

  const handleDateTimeChange = (fields: string) => (date: DateTime | null) => {
    const [dateField, timeField] = fields.split(":");

    setFormData((prevFormData: any) => ({
      ...prevFormData,
      [dateField]: date?.setZone("utc")?.toISODate() ?? null,
      [timeField]: date?.toLocaleString(DateTime.TIME_SIMPLE) ?? null,
    }));
  };

  const handleUsStateChange = (e: any) => {
    const flatStates = states.length > 51 ? states.slice(0, 50) : states;
    const { value } = e.target;
    const myState = flatStates.find((state: any) => state.id === parseInt(value));
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      stateId: Math.floor(value),
      state: myState,
    }));
  };

  const autofillVenueAddress = (e: any) => {
    const flatStates = states.length > 51 ? states.slice(0, 50) : states;
    const myState = flatStates.find((state: any) => state.id === parseInt(activeBusiness.stateId));
    setFormData((prev: any) => ({
      ...prev,
      street1: activeBusiness.street,
      city: activeBusiness.city,
      stateId: activeBusiness.stateId,
      state: myState,
      zip: activeBusiness.zip,
    }));
  };

  const renderImageFields = () => (
    <>
      <SettingsImageField
        title="Flyer Image"
        value="imageUrl"
        formData={formData}
        setFormData={setFormData}
        description="This image will be displayed as the flyer for the experience"
        dims={"1080"}
        height={"1920"}
      />
    </>
  );

  const renderFormField = (label: string, name: string, type = "text", placeholder = "") => (
    <TextField
      fullWidth
      sx={{ my: 0.5 }}
      label={label}
      name={name}
      value={formData[name] || ""}
      placeholder={placeholder}
      onChange={handleChange}
      multiline={type === "textarea"}
      minRows={type === "textarea" ? 4 : 1}
      type={type === "number" ? "number" : "text"}
      inputProps={
        type === "number"
          ? {
              min: 1,
              onKeyDown: (e) => e.key === "-" && e.preventDefault(),
            }
          : {}
      }
    />
  );

  const renderStateSelectField = () => (
    <FormControl fullWidth sx={{ my: 0.5 }}>
      <InputLabel id="state-select-label">State</InputLabel>
      <Select
        labelId="state-select-label"
        id="state-select"
        value={formData?.stateId ?? ""}
        onChange={handleUsStateChange}
        label="State"
      >
        {states?.map((state: { id: number; label: string }) => (
          <MenuItem value={state.id} key={state.id}>
            {state.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderEventSeriesDropdown = () => (
    <FormControl fullWidth sx={{ my: 0.5 }}>
      <InputLabel id="event-series-select-label">Event Series</InputLabel>
      <Select
        labelId="event-series-select-label"
        id="event-series"
        value={formData?.eventSeriesId ?? ""}
        onChange={handleChange}
        label="Event Series"
        name="eventSeriesId"
      >
        <MenuItem value={""}>None</MenuItem>
        {eventSeries?.map((series: { id: number; name: string }) => (
          <MenuItem value={series.id} key={series.id}>
            {series.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderDateSelect = (label: string, propName: string) => {
    const date = !formData[propName] ? null : DateTime.fromISO(formData[propName], { zone: "utc" });

    return (
      <DatePicker
        sx={{ my: 0.5, flex: 1 }}
        label={label}
        value={date}
        onChange={handleDateChange(propName)}
        disablePast
      />
    );
  };

  // TODO: Add must be future and compare to start/end dates to ensure events are ending after they start and aren't scheduled in the past
  const renderDateTimeSelect = (label: string, propName: string) => {
    const [dateField = null, timeField = null] = propName.split(":");

    const date =
      dateField && !formData[dateField] ? null : DateTime.fromISO(formData[dateField as string], { zone: "utc" });

    const time = timeField && !formData[timeField] ? null : convertTimeStringToObject(formData[timeField as string]);

    const dateTime = date && time ? date.set(time) : null;

    return (
      <DateTimePicker
        timezone="UTC"
        sx={{ my: 0.5, flex: 1 }}
        label={label}
        value={dateTime}
        onChange={handleDateTimeChange(propName)}
        minDateTime={DateTime.now().startOf("day")}
      />
    );
  };

  const renderExperienceDetails = () => {
    return (
      <div>
        <div style={{ display: "flex", flexDirection: "row", gap: 20 }}>
          {renderFormField("Guest Limit", "personRedeemLimit", "number")}
          {renderFormField("Seed Cost", "seedCost", "number")}
        </div>
        <Divider sx={{ my: 1 }} color={"grey.500"} />

        <div style={{ display: "flex", flexDirection: "row", gap: 20 }}>
          {renderDateSelect("Redeem Begin Date", "redeemBeginDate")}
          {renderDateSelect("Redeem End Date", "redeemEndDate")}
        </div>
        <div style={{ display: "flex", flexDirection: "row", gap: 20 }}>
          {renderDateTimeSelect("Experience Start", "eventBeginDate:startTime")}
          {renderDateTimeSelect("Experience End", "eventEndDate:endTime")}
        </div>
        <Divider sx={{ my: 1 }} color={"grey.500"} />
      </div>
    );
  };

  const renderZipStates = () => {
    return (
      <div style={{ display: "flex", flexDirection: "row", gap: 20 }}>
        {renderStateSelectField()}
        {renderFormField("Zip Code", "zip")}
      </div>
    );
  };

  const renderAutofillAddress = () => {
    return (
      businessType === "venue" && (
        <Button variant="contained" sx={{ my: 1 }} onClick={autofillVenueAddress}>
          Autofill Venue Address
        </Button>
      )
    );
  };

  const toggleIsDeleted = () => {
    setFormData((prev: any) => ({
      ...prev,
      isDeleted: !prev.isDeleted,
    }));
  };

  const renderFields = () => [
    renderFormField("Headline", "headline"),
    renderFormField("Description", "description", "textarea"),
    renderEventSeriesDropdown(),
    renderExperienceDetails(),
    renderFormField("Location Name", "locationName"),
    renderAutofillAddress(),
    renderFormField("Street", "street1"),
    renderFormField("City", "city"),
    renderZipStates(),
    renderFormField("Disclaimer", "disclaimer"),
  ];

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <Container fluid>
      <h2>{isNew ? "Create New Experience" : "Update Experience"}</h2>
      <div className={styles.formWrapper}>
        <div className={styles.formLeft}>{renderImageFields()}</div>
        <div className={styles.formRight}>
          <div className={styles.borderWrapper}>
            <h4 className={styles.innerHead}>Experience Details</h4>
            <div
              style={{
                width: "100%",
                height: 1,
                backgroundColor: "#ccc",
                marginBottom: 8,
              }}
            ></div>
            {renderFields()}

            {!isNew && (
              <div>
                {/* isDeleted field toggle */}
                <div>
                  <p onClick={toggleIsDeleted}>toggle</p>
                </div>
              </div>
            )}
            <div style={{ justifyContent: "flex-end", marginTop: 30 }} className={styles.searchFilterWrapper}>
              {experienceLoading ? (
                <LoadingSpinner />
              ) : (
                <button
                  style={{
                    backgroundColor: canSubmit || (isModified && !isNew) ? "#0F9547" : "#ccc",
                    border: "1px solid transparent",
                    color: "#fff",
                    padding: "10px 100px",
                    borderRadius: 50,
                  }}
                  onClick={isNew ? handleSubmit : handleUpdate}
                  disabled={isNew ? !canSubmit : !isModified}
                >
                  {isNew ? "Create Experience" : "Submit Updates"}
                </button>
              )}
              <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                  <Modal.Title>Submission Successful!</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p>
                    You can review/update this experience in the "Upcoming Experiences" tab if you need to make changes
                    to this experience
                  </p>
                </Modal.Body>
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};
